import { NotificationDto } from '../dtos/notification.dto';

/**
 * How a notification is presented, shared by the notifications page and the live toast so
 * the two never disagree on where a notification leads or what it looks like.
 *
 * <p>The server sends a `type` (`REQUEST`, `PARTICIPATION`) and the id of the thing the
 * notification is about. Everything visual is derived from those two here.
 */
export type NotificationKind = 'request' | 'attendance' | 'expense' | 'wallet';

export type NotificationDayGroup = 'TODAY' | 'YESTERDAY' | 'EARLIER';

export function notificationKind(n: Pick<NotificationDto, 'type'> | undefined): NotificationKind {
  switch (n?.type) {
    case 'PARTICIPATION':
      return 'attendance';
    case 'EXPENSE':
      return 'expense';
    case 'WALLET':
      return 'wallet';
    default:
      return 'request';
  }
}

/**
 * Where opening the notification goes. Attendance reminders carry a participation id, so
 * sending them to the request page (as the app used to) opened the wrong record.
 */
export function notificationRoute(n: Pick<NotificationDto, 'type' | 'objectId'> | undefined): unknown[] | null {
  // The wallet lives on the expenses page; the movement itself has no page of its own.
  if (notificationKind(n) === 'wallet') return ['/expenses'];
  if (!n?.objectId) return null;
  switch (notificationKind(n)) {
    case 'attendance':
      return ['/presence/detail', n.objectId];
    case 'expense':
      return ['/expenses/detail', n.objectId];
    default:
      return ['/request/detail', n.objectId];
  }
}

/** Icon (one outline path) and colours per kind, so the list and the toast look alike. */
const LOOKS: Record<NotificationKind, { icon: string; tone: string }> = {
  request: {
    icon: 'M9 12h6m-6 4h6M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z',
    tone: 'bg-accent-soft text-accent',
  },
  attendance: {
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    tone: 'bg-warning-soft text-warning-text',
  },
  expense: {
    icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z',
    tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
  },
  wallet: {
    icon: 'M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3',
    tone: 'bg-success-soft text-success-text',
  },
};

export function notificationIcon(n: Pick<NotificationDto, 'type'> | undefined): string {
  return LOOKS[notificationKind(n)].icon;
}

export function notificationTone(n: Pick<NotificationDto, 'type'> | undefined): string {
  return LOOKS[notificationKind(n)].tone;
}

const ACTION_KEYS: Record<NotificationKind, string> = {
  request: 'NOTIFICATION_TABLE.OPEN_REQUEST',
  attendance: 'NOTIFICATION_TABLE.OPEN_ATTENDANCE',
  expense: 'NOTIFICATION_TABLE.OPEN_EXPENSE',
  wallet: 'NOTIFICATION_TABLE.OPEN_WALLET',
};

const KIND_KEYS: Record<NotificationKind, string> = {
  request: 'NOTIFICATION_TABLE.KIND_REQUEST',
  attendance: 'NOTIFICATION_TABLE.KIND_ATTENDANCE',
  expense: 'NOTIFICATION_TABLE.KIND_EXPENSE',
  wallet: 'NOTIFICATION_TABLE.KIND_WALLET',
};

/** Transloco key for the link that opens the notification's subject. */
export function notificationActionKey(n: Pick<NotificationDto, 'type'> | undefined): string {
  return ACTION_KEYS[notificationKind(n)];
}

/** Transloco key for the small label naming what kind of notification this is. */
export function notificationKindKey(n: Pick<NotificationDto, 'type'> | undefined): string {
  return KIND_KEYS[notificationKind(n)];
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function notificationDayGroup(
  sendDate: string | Date | undefined,
  now: Date = new Date(),
): NotificationDayGroup {
  if (!sendDate) return 'EARLIER';
  const date = new Date(sendDate);
  if (isNaN(date.getTime())) return 'EARLIER';
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days <= 0) return 'TODAY';
  if (days === 1) return 'YESTERDAY';
  return 'EARLIER';
}

/**
 * Groups an already newest-first page into day sections, keeping the server's order inside
 * each one. Empty groups are left out.
 */
export function groupNotificationsByDay<T extends Pick<NotificationDto, 'sendDate'>>(
  items: T[],
  now: Date = new Date(),
): { group: NotificationDayGroup; items: T[] }[] {
  const order: NotificationDayGroup[] = ['TODAY', 'YESTERDAY', 'EARLIER'];
  const buckets = new Map<NotificationDayGroup, T[]>();
  for (const item of items) {
    const group = notificationDayGroup(item.sendDate, now);
    if (!buckets.has(group)) buckets.set(group, []);
    buckets.get(group)!.push(item);
  }
  return order.filter((g) => buckets.has(g)).map((group) => ({ group, items: buckets.get(group)! }));
}

/**
 * "5 minutes ago", "yesterday", "3 days ago" in the user's language; null under a minute,
 * so the caller can say "just now" in its own words. Past a week the exact date reads
 * better than a count of days.
 */
export function relativeTime(sendDate: string | Date | undefined, lang: string, now: Date = new Date()): string | null {
  if (!sendDate) return '';
  const date = new Date(sendDate);
  if (isNaN(date.getTime())) return '';
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return null;

  const rtf = new Intl.RelativeTimeFormat(lang || 'fr', { numeric: 'auto' });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return rtf.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return rtf.format(-hours, 'hour');
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days < 7) return rtf.format(-days, 'day');
  return new Intl.DateTimeFormat(lang || 'fr', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}
